// Simplified Question Builder Component - Basic question creation interface
import React, { useState } from "react";
import {
  QuestionType,
  Question,
  QuestionBuilderState,
} from "../../../types/question";
import { Comprehension } from "./QuestionTypes";

interface QuestionBuilderProps {
  // Question builder state & API (lifted from useQuestionBuilder)
  currentQuestion: QuestionBuilderState;
  questions: Question[];
  errors: Record<string, string>;
  updateQuestion: (field: keyof QuestionBuilderState, value: any) => void;
  updateTypeSpecificData: (field: string, value: any) => void;
  changeQuestionType: (type: QuestionType) => void;
  validateQuestion?: () => boolean;
  buildQuestion?: () => Question | null;
  addQuestion: () => boolean;
  updateQuestionAtIndex: (index: number, question: Question) => void;
  removeQuestion: (index: number) => void;
  moveQuestion: (fromIndex: number, toIndex: number) => void;
  resetBuilder?: () => void;
  loadQuestions?: (questions: Question[]) => void;
  getTotalPoints?: () => number;
  getEstimatedTime?: () => number;
  onQuestionsChange?: (questions: Question[]) => void;
  clearError?: (field: string) => void;
}

const QuestionBuilder: React.FC<QuestionBuilderProps> = ({
  currentQuestion,
  questions,
  errors,
  updateQuestion,
  updateTypeSpecificData,
  changeQuestionType,
  addQuestion,
  removeQuestion,
  moveQuestion,
  updateQuestionAtIndex,
  getTotalPoints,
  getEstimatedTime,
  clearError,
}) => {
  const [showQuestionList, setShowQuestionList] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const questionTypes: Array<{
    type: QuestionType;
    name: string;
    icon: string;
    description: string;
  }> = [
    {
      type: "multiple_choice",
      name: "Multiple Choice",
      icon: "🔘",
      description: "Single correct answer from options",
    },
    {
      type: "multiple_select",
      name: "Multiple Select",
      icon: "☑️",
      description: "Multiple correct answers",
    },
    {
      type: "true_false",
      name: "True/False",
      icon: "✅",
      description: "Simple true or false question",
    },
    {
      type: "short_answer",
      name: "Short Answer",
      icon: "📝",
      description: "Brief text response",
    },
    {
      type: "essay",
      name: "Essay",
      icon: "📄",
      description: "Long-form written response",
    },
    {
      type: "comprehension",
      name: "Reading Comprehension",
      icon: "📖",
      description: "Passage with multiple questions",
    },
  ];

  const renderBasicQuestionEditor = () => {
    switch (currentQuestion.type) {
      case "multiple_choice":
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Answer Options
              </label>
              {(
                currentQuestion.typeSpecificData.options || ["", "", "", ""]
              ).map((option: string, index: number) => (
                <div key={index} className="flex items-center space-x-2 mb-2">
                  <input
                    type="text"
                    value={option}
                    onChange={(e) => {
                      const newOptions = [
                        ...(currentQuestion.typeSpecificData.options || [
                          "",
                          "",
                          "",
                          "",
                        ]),
                      ];
                      newOptions[index] = e.target.value;
                      updateTypeSpecificData("options", newOptions);
                    }}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={`Option ${index + 1}`}
                  />
                  <input
                    type="radio"
                    name="correctAnswer"
                    checked={
                      currentQuestion.typeSpecificData.correctAnswer === option
                    }
                    onChange={() =>
                      updateTypeSpecificData("correctAnswer", option)
                    }
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm text-gray-500">Correct</span>
                </div>
              ))}
            </div>
          </div>
        );

      case "multiple_select":
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Answer Options (Select multiple correct answers)
              </label>
              {(
                currentQuestion.typeSpecificData.options || ["", "", "", ""]
              ).map((option: string, index: number) => (
                <div key={index} className="flex items-center space-x-2 mb-2">
                  <input
                    type="text"
                    value={option}
                    onChange={(e) => {
                      const newOptions = [
                        ...(currentQuestion.typeSpecificData.options || [
                          "",
                          "",
                          "",
                          "",
                        ]),
                      ];
                      newOptions[index] = e.target.value;
                      updateTypeSpecificData("options", newOptions);
                    }}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={`Option ${index + 1}`}
                  />
                  <input
                    type="checkbox"
                    checked={(
                      currentQuestion.typeSpecificData.correctAnswers || []
                    ).includes(option)}
                    onChange={(e) => {
                      const correctAnswers =
                        currentQuestion.typeSpecificData.correctAnswers || [];
                      const newCorrectAnswers = e.target.checked
                        ? [...correctAnswers, option]
                        : correctAnswers.filter(
                            (ans: string) => ans !== option
                          );
                      updateTypeSpecificData(
                        "correctAnswers",
                        newCorrectAnswers
                      );
                    }}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm text-gray-500">Correct</span>
                </div>
              ))}
            </div>
          </div>
        );

      case "true_false":
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Correct Answer
              </label>
              <div className="space-y-2">
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="tfAnswer"
                    value="True"
                    checked={
                      currentQuestion.typeSpecificData.correctAnswer === "True"
                    }
                    onChange={(e) =>
                      updateTypeSpecificData("correctAnswer", e.target.value)
                    }
                    className="w-4 h-4 text-blue-600"
                  />
                  <span>True</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="tfAnswer"
                    value="False"
                    checked={
                      currentQuestion.typeSpecificData.correctAnswer === "False"
                    }
                    onChange={(e) =>
                      updateTypeSpecificData("correctAnswer", e.target.value)
                    }
                    className="w-4 h-4 text-blue-600"
                  />
                  <span>False</span>
                </label>
              </div>
            </div>
          </div>
        );

      case "short_answer":
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Acceptable Answers (one per line)
              </label>
              <textarea
                value={(
                  currentQuestion.typeSpecificData.correctAnswers || []
                ).join("\n")}
                onChange={(e) => {
                  const answers = e.target.value
                    .split("\n")
                    .filter((ans: string) => ans.trim());
                  updateTypeSpecificData("correctAnswers", answers);
                }}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter acceptable answers, one per line"
              />
            </div>
          </div>
        );

      case "essay":
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Minimum Words
                </label>
                <input
                  type="number"
                  value={currentQuestion.typeSpecificData.minWords || ""}
                  onChange={(e) =>
                    updateTypeSpecificData(
                      "minWords",
                      e.target.value === "" ? "" : parseInt(e.target.value) || 0
                    )
                  }
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Maximum Words
                </label>
                <input
                  type="number"
                  value={currentQuestion.typeSpecificData.maxWords || ""}
                  onChange={(e) =>
                    updateTypeSpecificData(
                      "maxWords",
                      e.target.value === "" ? "" : parseInt(e.target.value) || 0
                    )
                  }
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>
        );

      case "comprehension":
        return (
          <Comprehension
            question={currentQuestion}
            updateQuestion={updateQuestion}
            updateTypeSpecificData={updateTypeSpecificData}
            errors={errors}
            clearError={clearError}
          />
        );

      default:
        return (
          <div className="text-center py-8 text-gray-500">
            Select a question type to configure options
          </div>
        );
    }
  };

  // Load an existing question into the builder for editing
  const handleEditQuestion = (index: number) => {
    const q = questions[index];
    if (!q) return;
    // populate builder fields from the question shape (top-level fields)
    const type = (q as any).type as any;
    changeQuestionType(type);
    updateQuestion("question", (q as any).question || "");
    updateQuestion("points", ((q as any).points as number) || 1);
    updateQuestion("difficulty", ((q as any).difficulty as any) || "easy");
    updateQuestion("explanation", ((q as any).explanation as string) || "");
    updateQuestion("tags", (q as any).tags || []);
    updateQuestion("estimatedTime", (q as any).estimatedTime || 60);

    // Map fields based on type
    switch (type) {
      case "multiple_choice":
        updateTypeSpecificData(
          "options",
          (q as any).options || ["", "", "", ""]
        );
        updateTypeSpecificData("correctAnswer", (q as any).correctAnswer || "");
        updateTypeSpecificData(
          "shuffleOptions",
          (q as any).shuffleOptions || false
        );
        break;
      case "multiple_select":
        updateTypeSpecificData(
          "options",
          (q as any).options || ["", "", "", ""]
        );
        updateTypeSpecificData(
          "correctAnswers",
          (q as any).correctAnswers || []
        );
        updateTypeSpecificData(
          "shuffleOptions",
          (q as any).shuffleOptions || false
        );
        break;
      case "true_false":
        updateTypeSpecificData(
          "correctAnswer",
          (q as any).correctAnswer || "True"
        );
        break;
      case "short_answer":
        updateTypeSpecificData(
          "correctAnswers",
          (q as any).correctAnswers || [""]
        );
        updateTypeSpecificData(
          "caseSensitive",
          (q as any).caseSensitive || false
        );
        updateTypeSpecificData("exactMatch", (q as any).exactMatch || false);
        break;
      case "essay":
        updateTypeSpecificData("minWords", (q as any).minWords || 0);
        updateTypeSpecificData("maxWords", (q as any).maxWords || 0);
        updateTypeSpecificData("rubric", (q as any).rubric || []);
        break;
      case "fill_blank":
        updateTypeSpecificData("questionText", (q as any).questionText || "");
        updateTypeSpecificData("blanks", (q as any).blanks || []);
        break;
      case "matching":
        updateTypeSpecificData("leftColumn", (q as any).leftColumn || []);
        updateTypeSpecificData("rightColumn", (q as any).rightColumn || []);
        updateTypeSpecificData(
          "correctMatches",
          (q as any).correctMatches || []
        );
        break;
      case "ordering":
        updateTypeSpecificData("items", (q as any).items || []);
        break;
      case "numeric":
        updateTypeSpecificData("correctAnswer", (q as any).correctAnswer || 0);
        updateTypeSpecificData("tolerance", (q as any).tolerance || 0);
        updateTypeSpecificData("unit", (q as any).unit || "");
        break;
      case "comprehension":
        updateTypeSpecificData("passage", (q as any).passage || "");
        updateTypeSpecificData("subQuestions", (q as any).subQuestions || []);
        break;
      default:
        break;
    }

    setEditingIndex(index);
    setShowQuestionList(true);
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    if (typeof (window as any) !== "undefined") {
      // prefer a provided resetBuilder if parent exposes it via props
      // the prop exists on the parent hook but is optional on this component
    }
    // Try to reset minimal fields
    if (typeof (updateQuestion as any) === "function") {
      updateQuestion("question", "");
      updateQuestion("points", 1);
      updateQuestion("difficulty", "easy");
      updateQuestion("explanation", "");
      updateTypeSpecificData("options", ["", "", "", ""]);
      updateTypeSpecificData("correctAnswers", []);
      updateTypeSpecificData("correctAnswer", undefined);
      updateTypeSpecificData("minWords", 0);
      updateTypeSpecificData("maxWords", 0);
    }
  };

  const handleSaveEdit = () => {
    if (editingIndex === null) return;
    const original = questions[editingIndex] || {};

    // Use the same logic as buildQuestion to ensure proper structure
    const baseUpdated = {
      ...((original as any)._id ? { _id: (original as any)._id } : {}),
      type: currentQuestion.type,
      question:
        currentQuestion.question ||
        (currentQuestion.type === "comprehension"
          ? "Read the passage below and answer the questions."
          : ""),
      points:
        currentQuestion.type === "comprehension"
          ? currentQuestion.typeSpecificData.subQuestions?.reduce(
              (sum: number, subQ: any) => sum + (subQ.points || 0),
              0
            ) || 1
          : currentQuestion.points,
      difficulty: currentQuestion.difficulty,
      explanation: currentQuestion.explanation,
      tags: currentQuestion.tags,
      estimatedTime: currentQuestion.estimatedTime,
    };

    let updated: Question;
    if (currentQuestion.type === "comprehension") {
      updated = {
        ...baseUpdated,
        passage: currentQuestion.typeSpecificData.passage || "",
        subQuestions: (currentQuestion.typeSpecificData.subQuestions || []).map(
          (subQ: any) => {
            // Remove temp _id fields to let Mongoose generate proper ObjectIds
            const { _id, ...subQWithoutId } = subQ;
            return subQWithoutId;
          }
        ),
      } as Question;
    } else {
      updated = {
        ...baseUpdated,
        ...currentQuestion.typeSpecificData,
      } as Question;
    }

    if (typeof updateQuestionAtIndex === "function") {
      updateQuestionAtIndex(editingIndex, updated);
    }
    setEditingIndex(null);
    // reset builder fields after save
    handleCancelEdit();
  };

  return (
    <div className="space-y-6">
      {/* Header with Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-blue-900">
              Create Quiz Questions
            </h3>
            <p className="text-sm text-blue-700 mt-1">
              Build your quiz by adding questions one at a time. Fill out the
              form below and click "Add Question" to include it in your quiz.
            </p>
          </div>
          <div className="text-right">
            <div className="text-sm font-medium text-blue-900">
              {questions.length} Questions Added
            </div>
            <div className="text-xs text-blue-700">
              {getTotalPoints ? getTotalPoints() : 0} Total Points
            </div>
          </div>
        </div>
      </div>

      {/* Question Type Selector */}
      <div>
        <h4 className="text-sm font-medium text-gray-900 mb-3">
          Step 1: Choose Question Type
        </h4>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {questionTypes.map((type) => (
            <button
              key={type.type}
              type="button"
              onClick={() => changeQuestionType(type.type)}
              className={`p-3 rounded-lg border-2 text-left transition-colors ${
                currentQuestion.type === type.type
                  ? "border-blue-500 bg-blue-50 text-blue-900"
                  : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50"
              }`}
            >
              <div className="flex items-center space-x-2 mb-1">
                <span className="text-lg">{type.icon}</span>
                <span className="font-medium text-sm">{type.name}</span>
              </div>
              <p className="text-xs text-gray-600">{type.description}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Basic Question Info */}
      <div>
        <h4 className="text-sm font-medium text-gray-900 mb-3">
          Step 2:{" "}
          {currentQuestion.type === "comprehension"
            ? "Configure Reading Comprehension"
            : "Enter Question Details"}
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {currentQuestion.type !== "comprehension" && (
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Question Text *
              </label>
              <textarea
                value={currentQuestion.question}
                onChange={(e) => updateQuestion("question", e.target.value)}
                rows={3}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.question ? "border-red-300" : "border-gray-300"
                }`}
                placeholder="Enter your question here..."
              />
              {errors.question && (
                <p className="mt-1 text-sm text-red-600">{errors.question}</p>
              )}
            </div>
          )}

          {currentQuestion.type === "comprehension" && (
            <div className="md:col-span-2">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>Reading Comprehension:</strong> Students will read the
                  passage you provide below and answer the questions you create.
                </p>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Points *
            </label>
            <input
              type="number"
              value={currentQuestion.points || ""}
              onChange={(e) => {
                const value = e.target.value;
                const numValue = value === "" ? "" : parseInt(value) || 1;
                updateQuestion("points", numValue);
              }}
              min="1"
              placeholder="1"
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.points ? "border-red-300" : "border-gray-300"
              }`}
            />
            {errors.points && (
              <p className="mt-1 text-sm text-red-600">{errors.points}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Difficulty
            </label>
            <select
              value={currentQuestion.difficulty}
              onChange={(e) =>
                updateQuestion(
                  "difficulty",
                  e.target.value as "easy" | "medium" | "hard"
                )
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="easy">Easy</option>
              <option value="medium">Medium</option>
              <option value="hard">Hard</option>
            </select>
          </div>
        </div>
      </div>

      {/* Type-Specific Editor */}
      <div>
        <h4 className="text-sm font-medium text-gray-900 mb-3">
          Step 3:{" "}
          {currentQuestion.type === "comprehension"
            ? "Add Questions"
            : "Configure Question Options"}
        </h4>
        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
          {renderBasicQuestionEditor()}
        </div>
      </div>

      {/* Explanation */}
      <div>
        <h4 className="text-sm font-medium text-gray-900 mb-3">
          Step 4: Add Explanation (Optional)
        </h4>
        <textarea
          value={currentQuestion.explanation}
          onChange={(e) => updateQuestion("explanation", e.target.value)}
          rows={2}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Explain why this is the correct answer (shown after quiz completion)"
        />
      </div>

      {/* Add Question Button */}
      <div className="bg-white border-2 border-blue-200 rounded-lg p-4">
        <div className="flex justify-between items-center">
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-1">
              Step 5: Add Question to Quiz
            </h4>
            <div className="text-sm text-gray-600">
              Questions: {questions.length} • Points:{" "}
              {getTotalPoints ? getTotalPoints() : 0} • Est. Time:{" "}
              {Math.ceil(
                ((getEstimatedTime ? getEstimatedTime() : 0) || 0) / 60
              )}
              min
            </div>
          </div>
          <div className="space-x-3">
            <button
              type="button"
              onClick={() => setShowQuestionList(!showQuestionList)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
            >
              {showQuestionList ? "Hide" : "Show"} Questions ({questions.length}
              )
            </button>
            {editingIndex === null ? (
              <button
                type="button"
                onClick={addQuestion}
                className="px-6 py-3 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 shadow-md"
              >
                ➕ Add Question
              </button>
            ) : (
              <div className="flex items-center space-x-2">
                <button
                  type="button"
                  onClick={handleSaveEdit}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700"
                >
                  Save Edit
                </button>
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Questions List */}
      {showQuestionList && questions.length > 0 && (
        <div className="border-t pt-6">
          <h4 className="text-lg font-medium text-gray-900 mb-4">
            Questions in Quiz
          </h4>
          <div className="space-y-3">
            {questions.map((question, index) => (
              <div
                key={index}
                className="bg-white border border-gray-200 rounded-lg p-4"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {question.type.replace("_", " ")}
                      </span>
                      <span className="text-sm text-gray-600">
                        {question.points} points
                      </span>
                      <span className="text-sm text-gray-600">
                        {question.difficulty}
                      </span>
                    </div>
                    <h5 className="font-medium text-gray-900 mb-1">
                      Question {index + 1}
                    </h5>
                    <p className="text-gray-700 text-sm">{question.question}</p>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      type="button"
                      onClick={() =>
                        moveQuestion(index, Math.max(0, index - 1))
                      }
                      disabled={index === 0}
                      className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
                      title="Move up"
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        moveQuestion(
                          index,
                          Math.min(questions.length - 1, index + 1)
                        )
                      }
                      disabled={index === questions.length - 1}
                      className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
                      title="Move down"
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      onClick={() => removeQuestion(index)}
                      className="p-1 text-red-400 hover:text-red-600"
                      title="Delete question"
                    >
                      🗑️
                    </button>
                    <button
                      type="button"
                      onClick={() => handleEditQuestion(index)}
                      className="p-1 text-blue-500 hover:text-blue-700"
                      title="Edit question"
                    >
                      ✏️
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestionBuilder;
